import React from 'react';
import { useIntl } from 'react-intl';

import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { queryClient } from 'src/lib/react-query';
import { toast } from 'src/hooks/useToast';
import { Discount } from 'src/features/discounts/utils/types';
import { useUpdateDiscount } from 'src/features/discounts/api/useUpdateDiscount';
import { LIST_DISCOUNTS_RESOURCE } from 'src/features/discounts/api/useListDiscounts';
import { DISCOUNT_RESOURCE } from 'src/features/discounts/api/useGetDiscount';
import DiscountFormModal from 'src/features/discounts/components/forms/DiscountFormModal';
import { discountsMessages } from 'src/features/discounts/utils/messages';

interface UpdateDiscountFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedDiscount: Discount;
    children?: React.ReactNode;
}

function UpdateDiscountFormModal<TData>({
    selectedDiscount,
    className,
    semantics,
    children,
}: UpdateDiscountFormModalProps<TData>) {
    const { formatMessage } = useIntl();

    const { mutate, isPending } = useUpdateDiscount({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_DISCOUNTS_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [DISCOUNT_RESOURCE] });
            toast({
                title: formatMessage(discountsMessages.updateDiscountSuccess),
                description: formatMessage(discountsMessages.updateDiscountSuccessDescription),
            });
        },
    });

    return (
        <DiscountFormModal
            className={className}
            mutate={(values, onSuccess, onError) =>
                mutate({ discountId: selectedDiscount.id, ...values }, { onSuccess, onError })
            }
            title={formatMessage(generalMessages.editEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(discountsMessages.editDiscountSubtitle)}
            selectedDiscount={selectedDiscount}
        >
            {children}
        </DiscountFormModal>
    );
}

export default UpdateDiscountFormModal;
